import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { MessageSquare, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface AdminLead {
    id: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    status: 'pending' | 'new' | 'contacted' | 'resolved';
    created_at: string;
}

const AdminLeads = () => {
    const [leads, setLeads] = useState<AdminLead[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadLeads = async () => {
        setIsLoading(true);
        const data = await safeFetch<AdminLead[]>(
            supabase.from('leads').select('*').order('created_at', { ascending: false }),
            [],
            'Loading enquiries'
        );
        setLeads(data);
        setIsLoading(false);
    };

    useEffect(() => {
        loadLeads();
    }, []);

    const updateStatus = async (id: string, newStatus: any) => {
        try {
            const { error } = await supabase
                .from('leads')
                .update({ status: newStatus })
                .eq('id', id);

            if (error) throw error;

            setLeads(leads.map(l => l.id === id ? { ...l, status: newStatus as any } : l));
            toast.success('Status updated');
        } catch (error: any) {
            toast.error('Error updating status');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Enquiries</h2>
                        <p className="text-slate-500 font-medium text-sm">Manage your website enquiries and messages here.</p>
                    </div>
                </div>

                <div className="grid gap-6">
                    {isLoading ? (
                        <div className="text-center py-20 bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 animate-pulse">Loading...</span>
                        </div>
                    ) : leads.length > 0 ? (
                        leads.map((lead) => (
                            <div key={lead.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-8 items-start">
                                <div className="w-16 h-16 rounded-2xl bg-accent/5 flex items-center justify-center flex-shrink-0 border border-accent/10">
                                    <MessageSquare className="w-8 h-8 text-accent" />
                                </div>

                                <div className="flex-1 space-y-6">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div>
                                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{lead.name}</h3>
                                            <div className="flex flex-wrap items-center gap-4 mt-2">
                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <Mail className="w-3 h-3" />
                                                    {lead.email}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <Phone className="w-3 h-3" />
                                                    {lead.phone}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(lead.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <select
                                                value={lead.status}
                                                onChange={(e) => updateStatus(lead.id, e.target.value)}
                                                className="h-10 px-4 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none focus:border-accent transition-all"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="new">New</option>
                                                <option value="contacted">Contacted</option>
                                                <option value="resolved">Resolved</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 italic text-slate-600 text-sm font-medium leading-relaxed">
                                        "{lead.message}"
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-20 bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">No enquiries found</span>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminLeads;
